import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { DraftsListItem } from 'src/features/plan-version-sidebar/drafts-list-item';
import { VersionsListItem } from 'src/features/plan-version-sidebar/versions-list-item';
import Collapse, { Panel } from 'src/shared/components/collapse/collapse';
import { useStore } from 'src/store';
import type { TPlanVersion, TActualPlansGroups } from 'src/store/comparison/types';
import { PlanVersionType } from 'src/store/comparison/types';
import { DRAFT_ASSIGNMENT } from 'src/store/drafts/types';
import {
  GOPLAN_DRILLING_CARPET_RENAME_PERSONAL_DRAFT,
  GOPLAN_DRILLING_CARPET_RENAME_PUBLIC_DRAFT,
  GOPLAN_DRILLING_CARPET_RENAME_PUBLISHED,
  GOPLAN_DRILLING_CARPET_SEND_PERSONAL_DRAFT_TO_INTEGRATION,
  GOPLAN_DRILLING_CARPET_SEND_TO_INTEGRATION,
} from 'src/store/roles-store/roles';

import styles from './versions-group.module.scss';

interface Props {
  versionsGroup: TActualPlansGroups;
  onVersionChange(planVersion: TPlanVersion): void;
  onDeletePlanVersion(planVersion: TPlanVersion): void;
  onDeleteDraftVersion(draftVersion: TPlanVersion): void;
  onMakeIntegrated?(planVersion: TPlanVersion): void;
}

export const VersionsGroup = observer(function VersionsGroup({
  versionsGroup,
  onVersionChange,
  onDeletePlanVersion,
  onDeleteDraftVersion,
  onMakeIntegrated,
}: Props) {
  const { t } = useTranslation();

  const { planVersion, rolesStore } = useStore();

  if (!versionsGroup.plans.length) {
    return null;
  }

  const currentVersionId = planVersion.version?.id;

  const isRemovablePlanVersionGroup = versionsGroup.name === 'actualPersonal';

  return (
    <Collapse key={versionsGroup.name} defaultActiveKey={versionsGroup.name}>
      <Panel key={versionsGroup.name} header={t(`drawers:planVersion.${versionsGroup.name}`)} className={styles.text}>
        <ul className={styles.versionsList}>
          {versionsGroup.plans.map((version, index) => {
            const isVersionRemovingAvailable = isRemovablePlanVersionGroup && version.id !== currentVersionId;
            const isCopyVersionAvailable = versionsGroup.name === 'actualPersonal';
            const isIntegrationAvailable = (() => {
              if (version.data.versionType === PlanVersionType.public) {
                return false;
              }

              if (rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_SEND_TO_INTEGRATION])) {
                return true;
              }

              if (version.data.versionType === PlanVersionType.personal) {
                return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_SEND_PERSONAL_DRAFT_TO_INTEGRATION]);
              }

              return false;
            })();

            const versionType = version.data.versionType;
            const isRenamingAvailable = (() => {
              if (version.data.versionType === PlanVersionType.draft) {
                return false;
              }

              if (versionType === PlanVersionType.personal) {
                return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_RENAME_PERSONAL_DRAFT]);
              }

              if (versionType === PlanVersionType.current) {
                return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_RENAME_PUBLISHED]);
              }

              if (versionType === PlanVersionType.public) {
                return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_RENAME_PUBLIC_DRAFT]);
              }

              return false;
            })();

            return versionsGroup.name === 'drafts' ? (
              <DraftsListItem
                key={index}
                version={version}
                onVersionChange={onVersionChange}
                onDeleteDraftVersion={
                  version.data.name.includes(DRAFT_ASSIGNMENT.import) ? undefined : onDeleteDraftVersion
                }
              />
            ) : (
              <VersionsListItem
                key={index}
                version={version}
                isRenamingAvailable={isRenamingAvailable}
                onVersionChange={onVersionChange}
                isPersonalDraftCreationAvailable={planVersion.isPersonalDraftCreationAvailable}
                onCopyVersion={isCopyVersionAvailable ? planVersion.onCopyVersion : undefined}
                onRename={isRenamingAvailable ? planVersion.renameVersion : undefined}
                onDeleteVersion={isVersionRemovingAvailable ? onDeletePlanVersion : undefined}
                onMakeIntegrated={isIntegrationAvailable ? onMakeIntegrated : undefined}
              />
            );
          })}
        </ul>
      </Panel>
    </Collapse>
  );
});
